import React from 'react';
import PropTypes from 'prop-types';

/**
 * Skeleton Loader Component
 * Provides loading placeholders for better UX
 */

const SkeletonBox = ({ width, height, className = '' }) => (
  <div 
    className={`skeleton-box ${className}`}
    style={{ width, height }}
    aria-hidden="true"
  />
);

SkeletonBox.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string
};

const SkeletonText = ({ lines = 1, className = '' }) => (
  <div className={`skeleton-text ${className}`} aria-hidden="true">
    {Array.from({ length: lines }, (_, index) => (
      <div 
        key={index} 
        className="skeleton-text__line"
        style={{ 
          width: index === lines - 1 ? '60%' : '100%' 
        }}
      />
    ))}
  </div>
);

SkeletonText.propTypes = {
  lines: PropTypes.number,
  className: PropTypes.string
};

const SkeletonButton = ({ width = '120px', height = '40px', className = '' }) => (
  <SkeletonBox 
    width={width} 
    height={height} 
    className={`skeleton-button ${className}`}
  />
);

SkeletonButton.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string
};

const SkeletonCard = ({ className = '' }) => (
  <div className={`skeleton-card ${className}`} aria-hidden="true">
    <SkeletonBox width="100%" height="200px" className="skeleton-card__image" />
    <div className="skeleton-card__content">
      <SkeletonText lines={2} />
      <div className="skeleton-card__actions">
        <SkeletonButton width="80px" height="32px" />
        <SkeletonButton width="100px" height="32px" />
      </div>
    </div>
  </div>
);

SkeletonCard.propTypes = {
  className: PropTypes.string
};

const SkeletonPDFViewer = ({ className = '' }) => (
  <div className={`skeleton-pdf-viewer ${className}`} aria-hidden="true">
    <div className="skeleton-pdf-viewer__toolbar">
      <SkeletonButton width="100px" height="36px" />
      <SkeletonButton width="80px" height="36px" />
      <SkeletonButton width="120px" height="36px" />
    </div>
    <div className="skeleton-pdf-viewer__controls">
      <SkeletonButton width="40px" height="40px" />
      <SkeletonBox width="120px" height="20px" />
      <SkeletonButton width="40px" height="40px" />
    </div>
    <SkeletonBox 
      width="100%" 
      height="600px" 
      className="skeleton-pdf-viewer__content"
    />
  </div>
);

SkeletonPDFViewer.propTypes = {
  className: PropTypes.string
};

const SkeletonToolbar = ({ className = '' }) => (
  <div className={`skeleton-toolbar ${className}`} aria-hidden="true">
    <div className="skeleton-toolbar__header">
      <SkeletonBox width="200px" height="40px" />
      <SkeletonButton width="40px" height="40px" />
    </div>
    <div className="skeleton-toolbar__tools">
      {Array.from({ length: 8 }, (_, index) => (
        <SkeletonButton 
          key={index} 
          width="50px" 
          height="50px" 
          className="skeleton-toolbar__tool"
        />
      ))}
    </div>
  </div>
);

SkeletonToolbar.propTypes = {
  className: PropTypes.string
};

// Main SkeletonLoader component with different variants
const SkeletonLoader = ({ variant = 'box', ...props }) => {
  switch (variant) {
    case 'text':
      return <SkeletonText {...props} />;
    case 'button':
      return <SkeletonButton {...props} />;
    case 'card':
      return <SkeletonCard {...props} />;
    case 'pdf-viewer':
      return <SkeletonPDFViewer {...props} />;
    case 'toolbar':
      return <SkeletonToolbar {...props} />;
    case 'box':
    default:
      return <SkeletonBox {...props} />;
  }
};

SkeletonLoader.propTypes = {
  variant: PropTypes.oneOf(['box', 'text', 'button', 'card', 'pdf-viewer', 'toolbar']),
  ...SkeletonBox.propTypes
};

export default SkeletonLoader;
export { SkeletonBox, SkeletonText, SkeletonButton, SkeletonCard, SkeletonPDFViewer, SkeletonToolbar };
